import {
  AreaWaitingTimeUpdatedEventV1Dto,
  BaseEventDto,
  CaseActionEventV1Dto,
  CaseCommentAddedEventV1Dto,
  CaseCreatedEventV1Dto,
  CaseExternalIdUpdatedEventV1Dto,
  CaseUpdatedEventV1Dto,
  LocationUpdatedEventV1Dto,
  TourCreatedEventV1Dto,
  TourDeletedEventV1Dto,
  TourUpdatedEventV1Dto,
} from '@tourfold/tf-api-client-frontend-v2';

/**
 * Server-sent event kinds emitted by the backend SSE pipeline.
 *
 * The naming mirrors the backend enum `<EVENT_TYPE>:V<VERSION>` to keep
 * interoperability between tenants and delivery channels.
 */
export const SSE_EVENT_KINDS = [
  'CASE_CREATED:V1',
  'CASE_UPDATED:V1',
  'CASE_ACTION:V1',
  'CASE_COMMENT_ADDED:V1',
  'CASE_EXTERNAL_ID_UPDATED:V1',
  'TOUR_CREATED:V1',
  'TOUR_UPDATED:V1',
  'TOUR_DELETED:V1',
  'LOCATION_UPDATED:V1',
  'VEHICLE_LOCATION_UPDATED:V1',
  'AREA_WAITING_TIME_UPDATED:V1',
  'HEARTBEAT:V1',
] as const;

export type SseEventKind = (typeof SSE_EVENT_KINDS)[number];

export const CASE_EVENT_KINDS: readonly SseEventKind[] = [
  'CASE_CREATED:V1',
  'CASE_UPDATED:V1',
  'CASE_ACTION:V1',
  'CASE_COMMENT_ADDED:V1',
  'CASE_EXTERNAL_ID_UPDATED:V1',
];

export const TOUR_EVENT_KINDS: readonly SseEventKind[] = [
  'TOUR_CREATED:V1',
  'TOUR_UPDATED:V1',
  'TOUR_DELETED:V1',
];

export const ACTIVITY_EVENT_KINDS: readonly SseEventKind[] = [
  'LOCATION_UPDATED:V1',
  'VEHICLE_LOCATION_UPDATED:V1',
  'AREA_WAITING_TIME_UPDATED:V1',
];

export type CaseEventKind = (typeof CASE_EVENT_KINDS)[number];
export type TourEventKind = (typeof TOUR_EVENT_KINDS)[number];
export type ActivityEventKind = (typeof ACTIVITY_EVENT_KINDS)[number];

/**
 * Heartbeat payload emitted by the backend for connection liveness checks.
 * The DTO is not part of the OpenAPI models because it is generated server-side.
 */
export interface HeartbeatEventDto extends BaseEventDto {
  kind: 'HEARTBEAT:V1';
  businessId: 'hb';
}

export type SseEventPayloadMap = {
  'CASE_CREATED:V1': CaseCreatedEventV1Dto;
  'CASE_UPDATED:V1': CaseUpdatedEventV1Dto;
  'CASE_ACTION:V1': CaseActionEventV1Dto;
  'CASE_COMMENT_ADDED:V1': CaseCommentAddedEventV1Dto;
  'CASE_EXTERNAL_ID_UPDATED:V1': CaseExternalIdUpdatedEventV1Dto;
  'TOUR_CREATED:V1': TourCreatedEventV1Dto;
  'TOUR_UPDATED:V1': TourUpdatedEventV1Dto;
  'TOUR_DELETED:V1': TourDeletedEventV1Dto;
  'LOCATION_UPDATED:V1': LocationUpdatedEventV1Dto;
  'VEHICLE_LOCATION_UPDATED:V1': LocationUpdatedEventV1Dto;
  'AREA_WAITING_TIME_UPDATED:V1': AreaWaitingTimeUpdatedEventV1Dto;
  'HEARTBEAT:V1': HeartbeatEventDto;
};

export type KnownSseEventDto = SseEventPayloadMap[SseEventKind];

export type SseEventPayload<K extends SseEventKind> = SseEventPayloadMap[K];

export function isSseEventKind(value: string): value is SseEventKind {
  return (SSE_EVENT_KINDS as readonly string[]).includes(value);
}

export function isHeartbeatEvent(event: BaseEventDto): event is HeartbeatEventDto {
  return event.kind === 'HEARTBEAT:V1';
}

